import type { XviewPerfTimingRegistry } from "./firstApiTiming";

export interface FirstScriptResourceInfo {
  url: string;
  startTime: number;
  responseEnd: number;
}

export interface FirstScriptResourceOptions {
  bufferSize?: number;
  onFirstScript?: (info: FirstScriptResourceInfo) => void;
}

const DEFAULT_BUFFER_SIZE = 500;

export function observeFirstScript(
  options: FirstScriptResourceOptions = {},
): void {
  if (typeof window === "undefined") return;
  if (typeof PerformanceObserver === "undefined") return;

  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER_SIZE;

  try {
    performance.setResourceTimingBufferSize?.(bufferSize);
  } catch {
    /* no-op */
  }

  const ns = (window.__xviewPerfTiming__ ??= {} as XviewPerfTimingRegistry);

  if (ns.firstScriptResponseEnd != null) return;

  const tryCapture = (entries: PerformanceResourceTiming[]): boolean => {
    if (ns.firstScriptResponseEnd != null) return true;

    const exclude = new Set(ns.excludedInstrumentationScriptUrls ?? []);

    const sorted = entries
      .filter((e) => e.initiatorType === "script")
      .filter((e) => !exclude.has(e.name))
      .sort((a, b) => a.startTime - b.startTime);

    const first = sorted[0];
    if (!first) return false;

    ns.firstScriptUrl = first.name;
    ns.firstScriptStartTime = first.startTime;
    ns.firstScriptResponseEnd = first.responseEnd;

    try {
      options.onFirstScript?.({
        url: first.name,
        startTime: first.startTime,
        responseEnd: first.responseEnd,
      });
    } catch {
      /* user callback errors are isolated */
    }

    return true;
  };

  const installObserver = (init: PerformanceObserverInit): boolean => {
    try {
      const observer = new PerformanceObserver((list, obs) => {
        if (tryCapture(list.getEntries() as PerformanceResourceTiming[])) {
          obs.disconnect();
        }
      });
      observer.observe(init);
      return true;
    } catch {
      return false;
    }
  };

  if (!installObserver({ type: "resource", buffered: true })) {
    installObserver({ entryTypes: ["resource"] });
  }
}
